import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Wrench, X, ArrowRight, Search, ChevronDown, Building2, Star } from 'lucide-react';
import type { AuftragsDaten } from '../../../lib/tickets/auftragsMail';
import { MODAL_HUELLE, modalKarte } from '../../../lib/ui/modalFlaeche';
import { sucheKontakte } from '../../../lib/tickets/meldenderKandidaten';
import { useViewport } from '../../../components/island/hooks';
import { istKompakt } from '../../../components/island/island-size';
import {
  ArrowRight as PhArrowRight, Buildings, Door, Info, Minus, Star as PhStar, TextAlignLeft, UserCircle, Wrench as PhWrench, X as PhX,
} from '@phosphor-icons/react';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FeldHuelle, FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormHinweis } from '../../../components/island/form/FormHinweis';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { FORM, ZEILEN_ICON } from '../../../components/island/form/form-stil';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DienstleisterOption {
  id: string;
  name: string;
  ansprechpartner: string | null;
  email: string | null;
  telefon: string | null;
  ist_bevorzugt?: boolean;
}

export interface EinheitOption {
  id: string;
  bezeichnung: string;
}

/** Auswählbarer Ansprechpartner — Mieter, Eigentümer oder der Absender der Quell-Mail. */
export interface MeldenderOption {
  id: string;
  name: string;
  rolle: string;
  telefon: string | null;
  email: string | null;
  /** Zusatzzeile im Auswahlmenü, z. B. die Einheit. */
  zusatz?: string | null;
}

interface Props {
  daten: AuftragsDaten;
  einheiten: EinheitOption[];
  einheitId: string | null;
  dienstleister: DienstleisterOption[];
  /** Kontakte zum Übernehmen (Telefon/E-Mail werden mit eingesetzt). */
  meldenderKandidaten?: MeldenderOption[];
  /** 'angebot' beschriftet den Dialog als Angebotsanfrage statt als Auftrag. */
  art?: 'auftrag' | 'angebot';
  onWeiter: (datenAktualisiert: AuftragsDaten) => void;
  onClose: () => void;
  /**
   * 'pille' rendert ohne Overlay/eigenen Header für den Island-Formularbereich
   * (dunkles Theme — die Pille liefert Titelzeile + Schließen), wie EmailVerfassen.
   */
  variant?: 'modal' | 'pille';
}

const ROLLEN = ['Mieter', 'Eigentümer', 'Hausmeister', 'Dienstleister', 'Sonstige'];

// Klassen-Paletten je Variante (hell = zentriertes Modal, dunkel = Pille)
const LABEL_MODAL = 'text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block';
const LABEL_PILLE = 'text-[9px] font-black text-white/40 uppercase tracking-widest mb-1.5 block';
const INPUT_MODAL =
  'w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1a1d24]/20 focus:border-[#1a1d24]/40';
const INPUT_PILLE =
  'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40';

function objektKurz(o: AuftragsDaten['objekt']): string {
  if (!o) return '';
  const z1 = [o.strasse, o.hausnummer].filter(Boolean).join(' ');
  const z2 = [o.plz, o.ort].filter(Boolean).join(' ');
  return [z1, z2].filter(Boolean).join(', ');
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function AuftragVorbereitenModal({
  daten, einheiten, einheitId, dienstleister, meldenderKandidaten = [], art = 'auftrag',
  onWeiter, onClose, variant = 'modal',
}: Props) {
  const angebot = art === 'angebot';
  const pille = variant === 'pille';
  const labelCls = pille ? LABEL_PILLE : LABEL_MODAL;
  const inputCls = pille ? INPUT_PILLE : INPUT_MODAL;
  const viewport = useViewport();
  const vollbild = istKompakt(viewport.width, viewport.height);

  // Handwerker vorauswählen: Match per E-Mail aus daten.dienstleister (KI-Vorschlag), sonst keiner
  const initialDlId =
    dienstleister.find((d) => d.email && daten.dienstleister?.email && d.email.toLowerCase() === daten.dienstleister.email.toLowerCase())?.id ?? '';

  const [dlId, setDlId] = useState<string>(initialDlId);
  const [dlSuche, setDlSuche] = useState('');
  const [dlOffen, setDlOffen] = useState(false);
  const dlBoxRef = useRef<HTMLDivElement>(null);

  const [einheitSel, setEinheitSel] = useState<string>(einheitId ?? '');

  const [mName, setMName] = useState(daten.meldender?.name ?? '');
  const [mRolle, setMRolle] = useState(daten.meldender?.rolle ?? '');
  const [mTelefon, setMTelefon] = useState(daten.meldender?.telefon ?? '');
  const [mEmail, setMEmail] = useState(daten.meldender?.email ?? '');
  // Beschreibung ist bewusst editierbar: bei einer Angebotsanfrage steht hier, WOFÜR das
  // Angebot gebraucht wird — die Ticket-/KI-Zusammenfassung ist nur der Startwert.
  const [beschreibung, setBeschreibung] = useState(daten.mangelBeschreibung ?? '');

  const uebernimm = (k: MeldenderOption) => {
    setMName(k.name);
    setMRolle(ROLLEN.includes(k.rolle) ? k.rolle : 'Sonstige');
    setMTelefon(k.telefon ?? '');
    setMEmail(k.email ?? '');
  };
  const uebernehmeKontakt = (id: string) => {
    const k = meldenderKandidaten.find((x) => x.id === id);
    if (k) uebernimm(k);
  };

  // #525: beliebigen Kontakt suchen (Mieter, Eigentümer, Dienstleister, Kontakte)
  const [kSuche, setKSuche] = useState('');
  const [kTreffer, setKTreffer] = useState<MeldenderOption[]>([]);
  const [kSucht, setKSucht] = useState(false);
  useEffect(() => {
    const q = kSuche.trim();
    if (q.length < 2) { setKTreffer([]); setKSucht(false); return; }
    let weg = false;
    setKSucht(true);
    const t = setTimeout(() => { sucheKontakte(q).then(r => { if (!weg) setKTreffer(r); }).catch(() => { if (!weg) setKTreffer([]); }).finally(() => { if (!weg) setKSucht(false); }); }, 300);
    return () => { weg = true; clearTimeout(t); };
  }, [kSuche]);
  const kandidatenGruppen = useMemo(() => {
    const m = new Map<string, MeldenderOption[]>();
    for (const k of meldenderKandidaten) {
      const g = k.id === 'vorbelegt' ? 'Aus der Meldung' : k.rolle === 'Sonstige' ? 'Kontakte' : k.rolle;
      m.set(g, [...(m.get(g) ?? []), k]);
    }
    return [...m.entries()];
  }, [meldenderKandidaten]);

  const gewaehlterDl = dienstleister.find((d) => d.id === dlId) ?? null;

  useEffect(() => {
    if (!dlOffen) return;
    const onDoc = (e: MouseEvent) => {
      if (dlBoxRef.current && !dlBoxRef.current.contains(e.target as Node)) setDlOffen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dlOffen]);

  const gefiltert = useMemo(() => {
    const q = dlSuche.trim().toLowerCase();
    const liste = q
      ? dienstleister.filter((d) =>
          [d.name, d.ansprechpartner, d.email].filter(Boolean).join(' ').toLowerCase().includes(q),
        )
      : dienstleister;
    return liste.slice(0, 50);
  }, [dlSuche, dienstleister]);

  const handleWeiter = () => {
    const gewaehlteEinheit = einheiten.find((e) => e.id === einheitSel) ?? null;
    const datenNeu: AuftragsDaten = {
      ...daten,
      einheit: gewaehlteEinheit ? gewaehlteEinheit.bezeichnung : daten.einheit,
      mangelBeschreibung: beschreibung.trim() || null,
      meldender: {
        name: mName.trim() || null,
        rolle: mRolle,
        telefon: mTelefon.trim() || null,
        email: mEmail.trim() || null,
      },
      dienstleister: gewaehlterDl
        ? { id: gewaehlterDl.id, name: gewaehlterDl.name, email: gewaehlterDl.email, telefon: gewaehlterDl.telefon }
        : daten.dienstleister,
    };
    onWeiter(datenNeu);
  };

  // ── Body (Variante steuert nur Farben, Logik identisch) ────────────────────
  const body = (
    <>
      {/* Kontext (read-only) */}
      <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${pille ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-[#1a1d24]/15'}`}>
        <Building2 className={`w-4 h-4 mt-0.5 shrink-0 ${pille ? 'text-[#c4a77d]' : 'text-[#1a1d24]'}`} />
        <div className="min-w-0 text-sm">
          <p className={`font-black truncate ${pille ? 'text-white' : 'text-gray-900'}`}>{objektKurz(daten.objekt) || 'Objekt unbekannt'}</p>
          <p className={`text-[12px] font-semibold truncate ${pille ? 'text-white/50' : 'text-gray-500'}`}>
            {[daten.kategorie, daten.eigentuemer].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
      </div>

      {/* Handwerker */}
      <div ref={dlBoxRef} className="relative">
        <label className={labelCls}>Handwerker (Empfänger)</label>
        <button
          type="button"
          onClick={() => setDlOffen((o) => !o)}
          className={`${inputCls} flex items-center justify-between text-left`}
        >
          <span className={gewaehlterDl ? (pille ? 'text-white' : 'text-gray-900') : (pille ? 'text-white/30' : 'text-gray-300')}>
            {gewaehlterDl
              ? `${gewaehlterDl.name}${gewaehlterDl.email ? ` · ${gewaehlterDl.email}` : ' · (keine E-Mail)'}`
              : 'Handwerker wählen …'}
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 ${pille ? 'text-white/40' : 'text-gray-400'}`} />
        </button>

        {dlOffen && (
          <div className={`absolute z-50 left-0 right-0 mt-1 rounded-xl shadow-xl overflow-hidden border ${pille ? 'bg-[#252a35] border-white/10' : 'bg-white border-gray-100'}`}>
            <div className={`flex items-center gap-2 px-3 py-2 border-b ${pille ? 'border-white/10' : 'border-gray-100'}`}>
              <Search className={`w-4 h-4 ${pille ? 'text-white/30' : 'text-gray-300'}`} />
              <input
                autoFocus
                value={dlSuche}
                onChange={(e) => setDlSuche(e.target.value)}
                placeholder="Name, Ansprechpartner oder E-Mail …"
                className={`w-full text-sm font-bold focus:outline-none bg-transparent ${pille ? 'text-white placeholder:text-white/30' : 'text-gray-900 placeholder:text-gray-300'}`}
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {gewaehlterDl && (
                <button
                  type="button"
                  onClick={() => { setDlId(''); setDlOffen(false); setDlSuche(''); }}
                  className={`w-full px-3 py-2 text-left text-[12px] font-bold ${pille ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-50'}`}
                >
                  ✕ Auswahl entfernen
                </button>
              )}
              {gefiltert.length === 0 && (
                <p className={`px-3 py-4 text-sm text-center ${pille ? 'text-white/40' : 'text-gray-400'}`}>Kein Handwerker gefunden.</p>
              )}
              {gefiltert.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => { setDlId(d.id); setDlOffen(false); setDlSuche(''); }}
                  className={`w-full px-3 py-2 text-left border-b last:border-0 ${pille ? 'hover:bg-white/5 border-white/5' : 'hover:bg-gray-50 border-gray-50'}`}
                >
                  <p className={`text-sm font-bold truncate flex items-center gap-1.5 ${pille ? 'text-white' : 'text-gray-900'}`}>
                    {d.ist_bevorzugt && (
                      <Star className={`w-3 h-3 shrink-0 ${pille ? 'text-[#c4a77d] fill-[#c4a77d]' : 'text-[#1a1d24] fill-[#1a1d24]'}`} />
                    )}
                    {d.name}
                  </p>
                  <p className={`text-[11px] truncate ${pille ? 'text-white/40' : 'text-gray-400'}`}>
                    {[d.ansprechpartner, d.email || '(keine E-Mail)'].filter(Boolean).join(' · ')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
        {gewaehlterDl && !gewaehlterDl.email && (
          <p className={`text-[11px] font-bold mt-1 ${pille ? 'text-amber-400' : 'text-amber-600'}`}>
            Dieser Handwerker hat keine E-Mail hinterlegt – bitte im nächsten Schritt eintragen.
          </p>
        )}
      </div>

      {/* Einheit */}
      <div>
        <label className={labelCls}>Wohnung / Einheit</label>
        {einheiten.length > 0 ? (
          <div className="relative">
            <select
              value={einheitSel}
              onChange={(e) => setEinheitSel(e.target.value)}
              className={`${inputCls} appearance-none pr-10`}
            >
              <option value="" className="text-gray-900">— keine / nachträglich ergänzen —</option>
              {einheiten.map((e) => (
                <option key={e.id} value={e.id} className="text-gray-900">{e.bezeichnung}</option>
              ))}
            </select>
            <ChevronDown className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${pille ? 'text-white/40' : 'text-gray-400'}`} />
          </div>
        ) : (
          <p className={`px-4 py-3 rounded-xl text-[12px] font-semibold border ${pille ? 'bg-white/5 border-white/10 text-white/40' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
            Für dieses Objekt sind keine Einheiten hinterlegt.
          </p>
        )}
      </div>

      {/* Was gemacht / angeboten werden soll — landet als {{auftrag.beschreibung}} in der Mail */}
      <div>
        <label className={labelCls}>
          {angebot ? 'Wofür wird ein Angebot benötigt?' : 'Gemeldeter Mangel'}
        </label>
        <textarea
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
          rows={4}
          placeholder={angebot
            ? 'z. B. Einbau eines Lüfters im Bad 1. OG, inkl. Anschluss'
            : 'Beschreibung des Mangels …'}
          className={`${inputCls} resize-y leading-relaxed`}
        />
        <p className={`text-[11px] mt-1 ${pille ? 'text-white/40' : 'text-gray-400'}`}>
          Steht so in der Mail — Vorschlag aus Ticket bzw. KI-Zusammenfassung.
        </p>
      </div>

      {/* Meldender */}
      <div>
        <label className={labelCls}>Kontaktdaten des Meldenden</label>
        {meldenderKandidaten.length > 0 && (
          <div className="relative mb-3">
            <select
              defaultValue=""
              onChange={(e) => { uebernehmeKontakt(e.target.value); e.currentTarget.value = ''; }}
              className={`${inputCls} appearance-none pr-10`}
            >
              <option value="" className="text-gray-900">Kontakt übernehmen …</option>
              {kandidatenGruppen.map(([gruppe, liste]) => (
                <optgroup key={gruppe} label={gruppe} className="text-gray-900">
                  {liste.map((k) => (
                    <option key={k.id} value={k.id} className="text-gray-900">
                      {[k.name, k.zusatz].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <ChevronDown className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${pille ? 'text-white/40' : 'text-gray-400'}`} />
          </div>
        )}
        {/* #525: jeden Kontakt finden – nicht nur die des Objekts */}
        <div className="relative mb-3">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${pille ? 'text-white/40' : 'text-gray-400'}`} />
          <input value={kSuche} onChange={(e) => setKSuche(e.target.value)} placeholder="Anderen Kontakt suchen (Mieter, Eigentümer, Dienstleister, Kontakt) …"
            aria-label="Kontakt suchen" className={`${inputCls} pl-9`} />
          {kTreffer.length > 0 && (
            <div role="listbox" className={`mt-1 max-h-56 overflow-y-auto rounded-xl ${pille ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
              {kTreffer.map((k) => (
                <button key={k.id} type="button" role="option" aria-selected={false}
                  onClick={() => { uebernimm(k); setKSuche(''); setKTreffer([]); }}
                  className={`w-full text-left px-3 py-2 flex flex-col ${pille ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-50 text-gray-900'}`}>
                  <span className="text-[13px] font-bold truncate">{k.name}</span>
                  <span className={`text-[11px] truncate ${pille ? 'text-white/50' : 'text-gray-500'}`}>
                    {[k.rolle === 'Sonstige' ? 'Kontakt' : k.rolle, k.zusatz, k.telefon, k.email].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          )}
          {kSuche.trim().length >= 2 && !kSucht && kTreffer.length === 0 && (
            <p className={`text-[11px] mt-1 ${pille ? 'text-white/40' : 'text-gray-400'}`}>Kein Kontakt gefunden.</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input value={mName} onChange={(e) => setMName(e.target.value)} placeholder="Name" className={inputCls} />
          <div className="relative">
            <select value={mRolle} onChange={(e) => setMRolle(e.target.value)} className={`${inputCls} appearance-none pr-10`}>
              <option value="" className="text-gray-900">Rolle …</option>
              {ROLLEN.map((r) => <option key={r} value={r} className="text-gray-900">{r}</option>)}
            </select>
            <ChevronDown className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${pille ? 'text-white/40' : 'text-gray-400'}`} />
          </div>
          <input value={mTelefon} onChange={(e) => setMTelefon(e.target.value)} placeholder="Telefon" className={inputCls} />
          <input value={mEmail} onChange={(e) => setMEmail(e.target.value)} placeholder="E-Mail" className={inputCls} />
        </div>
      </div>
    </>
  );

  const footer = (
    <>
      <button
        onClick={onClose}
        className={
          pille
            ? 'px-5 py-2.5 text-sm font-bold text-white/60 hover:text-white border border-white/15 rounded-xl transition-colors'
            : 'px-5 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors'
        }
      >
        Abbrechen
      </button>
      <button
        onClick={handleWeiter}
        className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-black text-white rounded-xl transition-colors hover:opacity-90 ${pille ? 'bg-[#c4a77d]' : 'bg-[#1a1d24]'}`}
      >
        Weiter zur Mail <ArrowRight className="w-4 h-4" />
      </button>
    </>
  );

  // ── Pillen-Variante (Handoff Pillen-Formulare): Bausteine, gleiche Zustände und Handler ──
  if (pille) {
    const neutral = { iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe };
    const leer = { icon: Minus, iconFlaeche: ZEILEN_ICON.leer.flaeche, iconFarbe: ZEILEN_ICON.leer.farbe };
    const kontext = [daten.kategorie, daten.eigentuemer].filter(Boolean).join(' · ') || '—';
    return (
      <FormRahmen knopfleiste={
        <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: onClose }}
          haupt={{ label: 'Weiter zur Mail', icon: PhArrowRight, iconRechts: true, typ: 'button', onClick: handleWeiter }} />
      }>
        {/* Kontext (read-only) */}
        <FormAbschnitt titel={objektKurz(daten.objekt) || 'Objekt unbekannt'} icon={Buildings} farbe="gold" index={0}>
          <FeldHuelle>
            <span className="flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 600, color: FORM.text2 }}>
              <Info aria-hidden size={13} weight="fill" className="shrink-0" />{kontext}
            </span>
          </FeldHuelle>
        </FormAbschnitt>

        <FormAbschnitt titel="Handwerker" icon={PhWrench} farbe="orange" index={1}>
          <FormAuswahl label="Handwerker (Empfänger)" wert={dlId || null}
            anzeige={gewaehlterDl ? `${gewaehlterDl.name}${gewaehlterDl.email ? ` · ${gewaehlterDl.email}` : ' · (keine E-Mail)'}` : undefined}
            anzeigeIcon={PhWrench} platzhalter="Handwerker wählen …"
            suchfeld={{ wert: dlSuche, onChange: setDlSuche, platzhalter: 'Name, Ansprechpartner oder E-Mail …' }}
            leerText="Kein Handwerker gefunden."
            zusatzZeile={gewaehlterDl ? { label: 'Auswahl entfernen', icon: PhX, onWaehlen: () => { setDlId(''); setDlSuche(''); } } : undefined}
            optionen={gefiltert.map((d) => ({
              wert: d.id, label: d.name, unter: [d.ansprechpartner, d.email || '(keine E-Mail)'].filter(Boolean).join(' · '),
              ...(d.ist_bevorzugt
                ? { icon: PhStar, iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe }
                : { icon: PhWrench, ...neutral }),
            }))}
            onChange={(id) => { setDlId(id); setDlSuche(''); }} />
          {gewaehlterDl && !gewaehlterDl.email && (
            <FormHinweis icon={Info} farbe="orange">Dieser Handwerker hat keine E-Mail hinterlegt – bitte im nächsten Schritt eintragen.</FormHinweis>
          )}
          {einheiten.length > 0 ? (
            <FormAuswahl label="Wohnung / Einheit" wert={einheitSel} onChange={setEinheitSel}
              optionen={[{ wert: '', label: '— keine / nachträglich ergänzen —', ...leer },
                ...einheiten.map((e) => ({ wert: e.id, label: e.bezeichnung, icon: Door, ...neutral }))]} />
          ) : (
            <FeldHuelle label="Wohnung / Einheit">
              <span style={{ fontSize: 13, fontWeight: 600, color: FORM.text2 }}>Für dieses Objekt sind keine Einheiten hinterlegt.</span>
            </FeldHuelle>
          )}
        </FormAbschnitt>

        {/* Was gemacht / angeboten werden soll — landet als {{auftrag.beschreibung}} in der Mail */}
        <FormAbschnitt titel={angebot ? 'Angebot' : 'Mangel'} icon={TextAlignLeft} farbe="violett" index={2}>
          <FormFeld label={angebot ? 'Wofür wird ein Angebot benötigt?' : 'Gemeldeter Mangel'} value={beschreibung} zeilen={4}
            onChange={(e) => setBeschreibung(e.target.value)}
            placeholder={angebot ? 'z. B. Einbau eines Lüfters im Bad 1. OG, inkl. Anschluss' : 'Beschreibung des Mangels …'}
            hinweis="Steht so in der Mail — Vorschlag aus Ticket bzw. KI-Zusammenfassung." />
        </FormAbschnitt>

        <FormAbschnitt titel="Kontaktdaten des Meldenden" icon={UserCircle} farbe="blau" index={3}>
          {meldenderKandidaten.length > 0 && (
            <FormAuswahl label="Kontakt übernehmen" wert={null} anzeige="Kontakt übernehmen …" anzeigeIcon={UserCircle}
              optionen={kandidatenGruppen.flatMap(([gruppe, liste]) => liste.map((k) => ({
                wert: k.id, label: [k.name, k.zusatz].filter(Boolean).join(' · '), gruppe, icon: UserCircle, ...neutral,
              })))}
              onChange={uebernehmeKontakt} />
          )}
          {/* #525: jeden Kontakt finden – nicht nur die des Objekts */}
          <FormSuche label="Kontakt suchen" platzhalter="Anderen Kontakt suchen (Mieter, Eigentümer, Dienstleister, Kontakt) …"
            wert={kSuche} onChange={(e) => setKSuche(e.target.value)} offen mindestLaenge={2} trimmen laedt={kSucht}
            leerText={kSucht ? undefined : 'Kein Kontakt gefunden.'} onSchliessen={() => setKTreffer([])}
            treffer={kTreffer.map((k) => ({
              id: k.id, label: k.name, icon: UserCircle, ...neutral,
              unter: [k.rolle === 'Sonstige' ? 'Kontakt' : k.rolle, k.zusatz, k.telefon, k.email].filter(Boolean).join(' · '),
            }))}
            onWaehlen={(id) => { const k = kTreffer.find((x) => x.id === id); if (k) { uebernimm(k); setKSuche(''); setKTreffer([]); } }} />
          <FormZeile>
            <FormFeld label="Name" value={mName} onChange={(e) => setMName(e.target.value)} placeholder="Name" />
            <FormAuswahl label="Rolle" wert={mRolle} onChange={setMRolle} platzhalter="Rolle …"
              optionen={[{ wert: '', label: 'Rolle …', ...leer }, ...ROLLEN.map((r) => ({ wert: r, label: r, icon: UserCircle, ...neutral }))]} />
          </FormZeile>
          <FormZeile>
            <FormFeld label="Telefon" value={mTelefon} onChange={(e) => setMTelefon(e.target.value)} placeholder="Telefon" />
            <FormFeld label="E-Mail" value={mEmail} onChange={(e) => setMEmail(e.target.value)} placeholder="E-Mail" />
          </FormZeile>
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // ── Modal-Variante ───────────────────────────────────────────────────────────
  return (
    <div className={`${MODAL_HUELLE} z-[300]`}>
      {/* Kein onClick: Klick daneben schloss diesen Dialog schon vor der Mobil-Etappe nie —
          Handwerker/Einheit/Meldender sind mühsam ausgewählt, ein versehentlicher Klick
          daneben soll sie nicht verwerfen. Absicht, keine vergessene Regression. */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={modalKarte('gross', 'max-w-[640px]', viewport, 'rounded-[2rem]')}>
        {/* Header */}
        <div className={`bg-[#1a1d24] p-6 flex items-center justify-between shrink-0 ${vollbild ? '' : 'rounded-t-[2rem]'}`}>
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2">
              <Wrench className="w-5 h-5 text-white/70" /> {angebot ? 'Angebotsanfrage vorbereiten' : 'Auftrag vorbereiten'}
            </h2>
            <p className="text-[9px] font-bold text-white/50 uppercase tracking-widest mt-0.5">
              Handwerker · Einheit · Meldender
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">{body}</div>

        {/* Footer */}
        <div className={`shrink-0 border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3 bg-white ${vollbild ? '' : 'rounded-b-[2rem]'}`}>
          {footer}
        </div>
      </div>
    </div>
  );
}
